import { Link } from "react-router-dom";
import { StatusBadge, Timestamp } from "@/design-system/components/ui";
import { WidgetFrame } from "../WidgetFrame";
import type { DashboardWidgetProps } from "../types";

/**
 * Generic renderers for the server-computed ("stat" / "queue") widgets: the
 * composite GET /api/dashboard payload carries the numbers/rows, and a small
 * per-key config here says how to present them. A missing payload renders the
 * degraded state — never a fake zero.
 */

type Payload = Record<string, unknown>;

function payloadOf(props: DashboardWidgetProps): Payload | null {
  const p = props.widget.payload;
  return p && typeof p === "object" ? (p as Payload) : null;
}

function num(payload: Payload, key: string): number {
  const v = payload[key];
  return typeof v === "number" ? v : 0;
}

// ---- Stat widgets ----------------------------------------------------------

export interface StatFigure {
  key: string;
  label: string;
  /** Tinted via the semantic status tokens when the value is non-zero. */
  tone?: "warning" | "error" | "info" | "ok";
}

export function compositeStat(config: { figures: StatFigure[]; to?: string; emptyText?: string }) {
  return function CompositeStatWidget(props: DashboardWidgetProps) {
    const payload = payloadOf(props);
    return (
      <WidgetFrame
        title={props.widget.title}
        subtitle={props.widget.subtitle}
        to={config.to}
        isError={payload === null}
      >
        {payload && (
          <div className="flex flex-wrap gap-x-space-5 gap-y-space-3">
            {config.figures.map((f) => (
              <div key={f.key}>
                <div
                  className={`font-display font-bold text-h2 leading-tight tabular-nums ${toneClass(
                    f.tone,
                    num(payload, f.key),
                  )}`}
                >
                  {num(payload, f.key).toLocaleString("en-US")}
                </div>
                <div className="font-display text-label uppercase tracking-label text-gray-mid">
                  {f.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </WidgetFrame>
    );
  };
}

function toneClass(tone: StatFigure["tone"], value: number): string {
  if (!tone || value === 0) return "text-onyx";
  switch (tone) {
    case "warning":
      return "text-status-warning";
    case "error":
      return "text-status-error";
    case "info":
      return "text-status-info";
    case "ok":
      return "text-status-success";
  }
}

// ---- Queue widgets ----------------------------------------------------------

export interface QueueRow {
  id: string;
  primary: string;
  secondary?: string | null;
  timestamp?: string | null;
  badge?: string | null;
  badgeTone?: "neutral" | "progress" | "info" | "warn" | "ok" | "danger";
  href?: string | null;
}

export function compositeQueue(config: {
  /** Maps one payload item (plus the whole payload) to a presentable row. */
  row: (item: Payload, payload: Payload) => QueueRow;
  /** Which payload property holds the rows (default "items"). */
  itemsKey?: string;
  /** Optional payload count property to headline (e.g. "count"). */
  countKey?: string;
  countLabel?: string;
  to?: string;
  emptyText?: string;
}) {
  return function CompositeQueueWidget(props: DashboardWidgetProps) {
    const payload = payloadOf(props);
    const rawItems = payload?.[config.itemsKey ?? "items"];
    const items = Array.isArray(rawItems) ? (rawItems as Payload[]) : [];
    const count = payload && config.countKey ? num(payload, config.countKey) : null;

    return (
      <WidgetFrame
        title={props.widget.title}
        subtitle={props.widget.subtitle}
        to={config.to}
        isError={payload === null}
        isEmpty={payload !== null && items.length === 0}
        emptyText={config.emptyText}
      >
        {count !== null && (
          <div className="font-display font-bold text-h2 text-onyx leading-tight tabular-nums mb-space-3">
            {count.toLocaleString("en-US")}
            {config.countLabel && (
              <span className="text-label font-semibold uppercase tracking-label text-gray-mid ml-space-2">
                {config.countLabel}
              </span>
            )}
          </div>
        )}
        <ul className="m-0 p-0 list-none divide-y divide-border-light">
          {items.map((item) => {
            const row = config.row(item, payload!);
            const body = (
              <div className="flex items-center justify-between gap-space-2 py-space-2">
                <div className="min-w-0">
                  <div className="text-small font-semibold text-onyx truncate">{row.primary}</div>
                  {row.secondary && (
                    <div className="text-label text-gray-mid truncate">{row.secondary}</div>
                  )}
                </div>
                <div className="flex items-center gap-space-2 shrink-0">
                  {row.badge && <StatusBadge tone={row.badgeTone ?? "neutral"}>{row.badge}</StatusBadge>}
                  {row.timestamp && (
                    <span className="text-label text-gray-mid">
                      <Timestamp value={row.timestamp} />
                    </span>
                  )}
                </div>
              </div>
            );
            return (
              <li key={row.id}>
                {row.href ? (
                  <Link to={row.href} className="block hover:bg-white-warm -mx-space-2 px-space-2 rounded-sm">
                    {body}
                  </Link>
                ) : (
                  body
                )}
              </li>
            );
          })}
        </ul>
      </WidgetFrame>
    );
  };
}
